/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */

/**
 * The container component for widgets
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.12.0
 * @since 1.0.1
 */

import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { FastActionsPlate } from '@/main/components/FastActions/FastActionsPlate';
import { initTranslationFastActions } from '@/main/modules/internationalization/constants/components/fastActions/fastActions';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import initTranslationCommon from '@modules/internationalization/constants/common';
import initTranslationPageDashboard from '@modules/internationalization/constants/pages/dashboard/dashboard';
import ICommonTranslation from '@modules/internationalization/types/common';
import IPageDashboardTranslation from '@modules/internationalization/types/pages/dashboard/dashboard';

import { EDraggableID, IDraggableWidget, IDraggableWidgetArray } from '../../types/dragAndDropTypes';
import { IDragMoveResult } from '../DragAndDrop/dndContainerTypes';
import DragAndDropContainer from '../DragAndDrop/DragAndDropContainer';
import { WidgetMainContainerStyled } from './styled/WidgetContainerStyled';
import WidgetCosts from './WidgetCosts/components/WidgetCosts';
import WidgetMultiBalances from './WidgetMultiBalances/components/WidgetMultiBalances';
import WidgetOrderInquiries from './WidgetOrderInquiries/components/WidgetOrderInquiries';
import WidgetPaymentHistory from './WidgetPaymentHistory/components/WidgetPaymentHistory';
import WidgetPersonalAccount from './WidgetPersonalAccount/components/WidgetPersonalAccount';
import { WidgetReportsConstructor } from './WidgetReportsConstructor/WidgetReportsConstructor';
import WidgetSearchAccounts from './WidgetSearchAccounts/components/WidgetSearchAccounts';
import WidgetSubscriberMobile from './WidgetSubscriberMobile/components/WidgetSubscriberMobile';
import WidgetSubscriberMaxCosts from './WidgetSubscribersMaxCosts/components/WidgetSubscriberMaxCosts';
import { EWidgetNames, IWidgetMetaItem, TWidgetMetaList } from './widgetTypes';
import WidgetUsefulLinks from './WidgetUsefulLinks/WidgetUsefulLinks';
import WidgetVoting from './WidgetVoting/components/WidgetVoting';

export interface IWidgetContainerProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
    widgetMeta: TWidgetMetaList;
}

export const Widgets: IDraggableWidgetArray = {
    [EWidgetNames.PERSONAL_ACCOUNT]: {
        column: 0,
        id: EWidgetNames.PERSONAL_ACCOUNT,
        order: 0,
        render: () => <WidgetPersonalAccount />,
    },
    [EWidgetNames.PAYMENT_HISTORY]: {
        column: 0,
        id: EWidgetNames.PAYMENT_HISTORY,
        order: 1,
        render: () => <WidgetPaymentHistory />,
    },
    [EWidgetNames.MULTI_BALANCES]: {
        column: 0,
        id: EWidgetNames.MULTI_BALANCES,
        order: 2,
        render: () => <WidgetMultiBalances />,
    },
    [EWidgetNames.COSTS]: {
        column: 1,
        id: EWidgetNames.COSTS,
        order: 0,
        render: () => <WidgetCosts />,
    },
    [EWidgetNames.SUBSCRIBER_MOBILE]: {
        column: 1,
        id: EWidgetNames.SUBSCRIBER_MOBILE,
        order: 1,
        render: () => <WidgetSubscriberMobile />,
    },
    [EWidgetNames.SUBSCRIBERS_MAX_COSTS]: {
        column: 2,
        id: EWidgetNames.SUBSCRIBERS_MAX_COSTS,
        order: 0,
        render: () => <WidgetSubscriberMaxCosts />,
    },
    [EWidgetNames.ORDERS_INQUIRIES]: {
        column: 2,
        id: EWidgetNames.ORDERS_INQUIRIES,
        order: 2,
        render: () => <WidgetOrderInquiries />,
    },
    [EWidgetNames.USEFUL_LINKS]: {
        column: 2,
        id: EWidgetNames.USEFUL_LINKS,
        order: 1,
        render: () => <WidgetUsefulLinks />,
    },
    [EWidgetNames.REPORTS_CONSTRUCTOR]: {
        column: 1,
        id: EWidgetNames.REPORTS_CONSTRUCTOR,
        order: 0,
        render: () => <WidgetReportsConstructor />,
    },
    [EWidgetNames.VOTING]: {
        column: 2,
        id: EWidgetNames.VOTING,
        order: 3,
        render: () => <WidgetVoting />,
    },
};

const defaultProps: Partial<IWidgetContainerProps> = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon, ...initTranslationFastActions },
};

const WidgetContainer = (props: IWidgetContainerProps) => {

    const isSubsInRoamingEnabled = 
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.SUBSCRIBERS_IN_ROAMING_NOTIFICATION));

    const [widgetsFirst, setWidgetsFirst] = useState<IDraggableWidget[]>([]);
    const [widgetsSecond, setWidgetsSecond] = useState<IDraggableWidget[]>([]);
    const [widgetsThird, setWidgetsThird] = useState<IDraggableWidget[]>([]);

    useEffect(() => {
        setInitialWidgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.widgetMeta]);

    const dragAndDropColumns = {
        [EDraggableID.FIRST_ITEMS]: widgetsFirst,
        [EDraggableID.SECOND_ITEMS]: widgetsSecond,
        [EDraggableID.THIRD_ITEMS]: widgetsThird,
    };

    const sortColumn = (a: IDraggableWidget, b: IDraggableWidget): number => a.order - b.order;

    const setInitialWidgets = () => {
        const firstColumn: IDraggableWidget[] = [];
        const secondColumn: IDraggableWidget[] = [];
        const thirdColumn: IDraggableWidget[] = [];
        props.widgetMeta.forEach((widget: IWidgetMetaItem) => {
            const draggableWidget = Widgets[widget.name];
            if (!(draggableWidget && draggableWidget.column !== undefined)) return;
            switch (draggableWidget.column) {
                case 0:
                    firstColumn.push(draggableWidget);
                    break;
                case 1:
                    secondColumn.push(draggableWidget);
                    break;
                case 2:
                    thirdColumn.push(draggableWidget);
                    break;
                default:
                    break;
            }
        });

        firstColumn.sort(sortColumn);
        secondColumn.sort(sortColumn);
        thirdColumn.sort(sortColumn);

        // move widget left if column empty
        if (firstColumn.length === 0) {
            setWidgetsFirst(secondColumn);
            setWidgetsSecond(thirdColumn);
        } else if (secondColumn.length === 0) {
            setWidgetsFirst(firstColumn);
            setWidgetsSecond(thirdColumn);
        } else {
            setWidgetsFirst(firstColumn);
            setWidgetsSecond(secondColumn);
            setWidgetsThird(thirdColumn);
        }

    };

    const saveMoveResult = (result: IDragMoveResult<IDraggableWidget>) => {
        if (result.result.firstItems) {
            setWidgetsFirst(result.result.firstItems);
        }
        if (result.result.secondItems) {
            setWidgetsSecond(result.result.secondItems);
        }
        if (result.result.thirdItems) {
            setWidgetsThird(result.result.thirdItems);
        }
    };

    const saveReorderResult = (items: IDraggableWidget[], id: string) => {
        switch (id) {
            case EDraggableID.FIRST_ITEMS:
                setWidgetsFirst(items);
                break;
            case EDraggableID.SECOND_ITEMS:
                setWidgetsSecond(items);
                break;
            case EDraggableID.THIRD_ITEMS:
                setWidgetsThird(items);
                break;
            default:
                break;
        }
    };

    return (
        <WidgetMainContainerStyled>
            {isSubsInRoamingEnabled && <FastActionsPlate />}

            <WidgetSearchAccounts
                translation={props.translation}
            />

            <DragAndDropContainer
                columns={dragAndDropColumns}
                isDropDisable={true}
                saveMoveResult={saveMoveResult}
                saveReorderResult={saveReorderResult}
                translation={props.translation}
            />
        </WidgetMainContainerStyled>
    );
};

WidgetContainer.defaultProps = defaultProps;

export default WidgetContainer;

